/**
 * ThemeContext — Phase 4.3 wave 1.
 *
 * Provides per-portal token overrides on top of the static
 * `@realtyflows/ui` design tokens. Components that opt-in via the
 * `useTheme()` hook get a token object where the brand colors (gold /
 * navy / etc.) are replaced by the active team's `primary_color` /
 * `accent_color` when those are set.
 *
 * Migration strategy is deliberately gradual. Existing components keep
 * `import { t } from "@realtyflows/ui"` and continue to render the
 * RealtyFlows defaults — nothing breaks. New code, or the components we
 * sweep over time, switch to `useTheme()` and become brand-aware.
 *
 * What overrides what:
 *   - team.primary_color → gold / emerald / amber / goldLight (anything
 *     that visually carries the "RealtyFlows primary action" weight)
 *   - team.accent_color  → navy / textPrimary (the dark UI accent)
 *
 * Other tokens are left untouched — text contrast, error coral, and the
 * neutral surface colors stay constant across brands. That's intentional;
 * we don't want a brokerage to pick a low-contrast theme and break
 * accessibility on their agents.
 *
 * The personal portal (team_id == null) is the always-defaults branch.
 */
import { createContext, useContext, useMemo } from "react";
import { t as defaultTokens } from "@realtyflows/ui";

// Context value is { tokens, activeMembership }. Components can read
// either piece via the convenience hooks below.
const ThemeContext = createContext({ tokens: defaultTokens, activeMembership: null });

/**
 * Build the merged token object for an active membership. Pure function;
 * the provider memoizes the result so consumers don't re-render unless
 * the active portal actually changes.
 */
function buildTokens(activeMembership) {
  if (!activeMembership) return defaultTokens;
  const primary = activeMembership.primary_color;
  const accent  = activeMembership.accent_color;
  if (!primary && !accent) return defaultTokens;

  return {
    ...defaultTokens,
    ...(primary && {
      gold:        primary,
      emerald:     primary,           // alias — same value in the default set
      amber:       primary,
      goldLight:   primary + "33",    // ~20% opacity for hover/wash backgrounds
      goldPale:    primary + "1A",    // ~10% for very-soft accents
      emeraldLight: primary + "1A",
    }),
    ...(accent && {
      navy:        accent,
      navyLight:   accent + "B3",     // ~70% (used for sidebar bg variants)
      textPrimary: accent,
    }),
  };
}

/**
 * <ThemeProvider activeMembership={...}>
 *
 * Wrap once at the app root. Pass the user's currently-active membership
 * (the entry from `user.memberships` whose `team_id` matches `active_team_id`).
 * Null/undefined → defaults.
 */
export function ThemeProvider({ activeMembership, children }) {
  const tokens = useMemo(() => buildTokens(activeMembership), [
    activeMembership?.team_id,
    activeMembership?.primary_color,
    activeMembership?.accent_color,
  ]);
  const value = useMemo(() => ({ tokens, activeMembership }), [tokens, activeMembership]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * useTheme() — token object merged with the active portal's branding.
 *
 * Use exactly like the static `t` from `@realtyflows/ui`, but inside a
 * function component:
 *
 *   const t = useTheme();
 *   <div style={{ background: t.gold }}>...
 *
 * Outside a component (utility code), keep importing `t` directly —
 * those values stay as the global defaults.
 */
export function useTheme() {
  return useContext(ThemeContext).tokens;
}

/**
 * useActivePortal() — returns the active membership object, or null
 * when there's no portal context (logged out, or portal switcher
 * hasn't selected one yet).
 *
 * Use this when a component needs to know not just the colors but
 * which team it's rendering for (e.g. "this is team X's clients").
 */
export function useActivePortal() {
  return useContext(ThemeContext).activeMembership;
}

/**
 * usePortalLink() — returns a function that prepends the active
 * portal's URL prefix (`/p/<slug>`) to an absolute path.
 *
 * Use everywhere navigation happens inside the app. Once Phase 4.4 is
 * fully rolled out, the URL bar always carries the portal prefix so
 * the address is shareable + reflects what the user is looking at.
 *
 * Falls back to returning the path unchanged when:
 *   - there's no active membership (logged out / no portal context)
 *   - the membership has no `team_slug` set (graceful — older teams
 *     might predate the slug column being populated)
 *   - the path is already absolute under `/p/...` (no double-prefix)
 *   - the path starts with `http(s)://` or `mailto:` (external)
 */
export function usePortalLink() {
  const m = useActivePortal();
  const slug = m && (m.team_id == null ? "personal" : (m.team_slug || null));
  return (path) => {
    if (!path || typeof path !== "string") return path;
    if (!slug) return path;
    if (/^([a-z]+:|\/\/|\/p\/)/i.test(path)) return path;
    if (!path.startsWith("/")) return path;
    return `/p/${slug}${path}`;
  };
}
